/**
 * Ceondent-only motion layer. Keeping this outside the shared service widget
 * prevents the clinic animation from leaking into other service sites.
 */
export async function mountCeondentAnimation(host: HTMLElement): Promise<() => void> {
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const compactViewport = window.matchMedia('(max-width: 920px)').matches;
  const { gsap } = await import('gsap');
  const { ScrollTrigger } = await import('gsap/ScrollTrigger');
  gsap.registerPlugin(ScrollTrigger);
  const hero = host.querySelector<HTMLElement>('.service-hero');
  if (!hero) return () => undefined;
  const pointerCleanups: Array<() => void> = [];
  const context = gsap.context(() => {
    const copy = hero.querySelectorAll('.hero-copy>*');
    const stage = hero.querySelector<HTMLElement>('.hero-stage');
    const smilePaths = hero.querySelectorAll<SVGPathElement>('.smile-line path');
    const particles = hero.querySelectorAll('.clinic-particle');
    const sections = host.querySelectorAll<HTMLElement>('.showcase-block,app-appointment-widget,app-blog-home-widget');
    if (reduceMotion) {
      gsap.set([copy, stage, smilePaths, particles, sections], { clearProps: 'all', autoAlpha: 1 });
      return;
    }
    smilePaths.forEach((path) => {
      const length = path.getTotalLength();
      gsap.set(path, { strokeDasharray: length, strokeDashoffset: length });
    });
    gsap.set(copy, { autoAlpha: 0, y: 34, filter: 'blur(9px)' });
    gsap.set(stage, { autoAlpha: 0, scale: .78, rotate: 7, y: 42 });
    gsap.set(particles, { autoAlpha: 0, scale: 0 });
    gsap.timeline({ defaults: { ease: 'power3.out' } })
      .fromTo(hero, { clipPath: 'inset(0 0 100% 0 round 44px)' }, { clipPath: 'inset(0 0 0% 0 round 44px)', duration: 1.05, ease: 'power4.inOut' }, 0)
      .to(copy, { autoAlpha: 1, y: 0, filter: 'blur(0px)', stagger: .11, duration: .9 }, .32)
      .to(stage, { autoAlpha: 1, scale: 1, rotate: 0, y: 0, duration: 1.35, ease: 'expo.out' }, .38)
      .to(smilePaths, { strokeDashoffset: 0, stagger: .16, duration: 1.75, ease: 'power2.inOut' }, .5)
      .to(particles, { autoAlpha: .75, scale: 1, stagger: { each: .035, from: 'random' }, duration: .6 }, .82);
    gsap.to(hero.querySelector('.clinic-aurora--one'), { xPercent: -12, yPercent: 10, scale: 1.12, duration: 8, ease: 'sine.inOut', repeat: -1, yoyo: true });
    gsap.to(hero.querySelector('.clinic-aurora--two'), { xPercent: 16, yPercent: -12, scale: .9, duration: 10, ease: 'sine.inOut', repeat: -1, yoyo: true });
    if (!compactViewport) {
      gsap.to(hero.querySelector('.clinic-aurora--three'), { xPercent: 330, duration: 7.5, ease: 'power1.inOut', repeat: -1, repeatDelay: 2.5 });
      gsap.to(hero.querySelector('.light-scan'), { xPercent: 780, duration: 5.6, ease: 'power1.inOut', repeat: -1, repeatDelay: 3 });
      gsap.to(hero.querySelector('.enamel-ring'), { rotate: 360, duration: 42, ease: 'none', repeat: -1 });
    }
    gsap.to(particles, { y: 'random(-16,16)', x: 'random(-12,12)', autoAlpha: 'random(.25,.8)', duration: 'random(2.5,5)', repeat: -1, yoyo: true, stagger: { each: .08, from: 'random' }, ease: 'sine.inOut' });
    sections.forEach((section, index) => gsap.from(section, { autoAlpha: 0, y: Math.min(72, 38 + index * 5), scale: .985, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: section, start: 'top 86%', once: true } }));
    host.querySelectorAll<HTMLElement>('.service-grid,.team-grid,.branch-grid').forEach((grid) => gsap.from(grid.children, { autoAlpha: 0, y: 45, stagger: .08, duration: .75, ease: 'power3.out', scrollTrigger: { trigger: grid, start: 'top 82%', once: true } }));
    if (stage && !compactViewport) gsap.to(stage, { yPercent: 10, rotate: -2, scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 1 } });
  }, host);
  if (window.matchMedia('(pointer:fine)').matches) {
    const stage = hero.querySelector<HTMLElement>('.hero-stage');
    if (stage) {
      const move = (event: PointerEvent) => {
        const bounds = hero.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - .5;
        const y = (event.clientY - bounds.top) / bounds.height - .5;
        hero.style.setProperty('--light-x', `${Math.round((x + .5) * 100)}%`);
        hero.style.setProperty('--light-y', `${Math.round((y + .5) * 100)}%`);
        gsap.to(stage, { x: x * 18, y: y * 14, rotateY: x * 5, rotateX: -y * 4, duration: .9, ease: 'power2.out', overwrite: true });
      };
      const leave = () => gsap.to(stage, { x: 0, y: 0, rotateX: 0, rotateY: 0, duration: 1.2, ease: 'power3.out' });
      hero.addEventListener('pointermove', move);
      hero.addEventListener('pointerleave', leave);
      pointerCleanups.push(() => hero.removeEventListener('pointermove', move), () => hero.removeEventListener('pointerleave', leave));
    }
  }
  return () => { pointerCleanups.forEach((cleanup) => cleanup()); context.revert(); ScrollTrigger.refresh(); };
}
